import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuthToggleContext } from "../../components/providers/AuthProvider.jsx";
import { SlideCard, BrandButton, TextInput } from "../../components/ui/index.js";
import { Eye, EyeOff, Mail, Lock, User, MailCheck } from "lucide-react";
import { showAlert } from "../../components/HelperAlerts/HelpersAlerts.jsx";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Register() {
    const { handleRegister } = useAuthToggleContext();

    const [name, setName]             = useState('');
    const [email, setEmail]           = useState('');
    const [password, setPassword]     = useState('');
    const [password2, setPassword2]   = useState('');
    const [showPass, setShowPass]     = useState(false);
    const [showPass2, setShowPass2]   = useState(false);
    const [registered, setRegistered] = useState(false);

    const validateForm = () => {
        if (!name.trim()) return "Introduce tu nombre.";
        if (!email.trim()) return "Introduce tu email.";
        if (!EMAIL_PATTERN.test(email.trim())) return "Introduce un email válido.";
        if (!password) return "Introduce una contraseña.";
        if (!password2) return "Repite la contraseña.";
        if (password !== password2) return "Las contraseñas no coinciden.";
        return null;
    };

    const submit = async (e) => {
        e.preventDefault();
        const validationMessage = validateForm();
        if (validationMessage) {
            showAlert({
                title: "Revisa el registro",
                text: validationMessage,
                icon: "error",
            });
            return;
        }
        const ok = await handleRegister({ name, email, password, password2 });
        if (ok) setRegistered(true);
    };

    const successContent = (
        <>
            <div className="flex flex-col items-center gap-3 py-4">
                <MailCheck size={48} className="text-primary" />
                <h2 className="text-2xl font-extrabold dark:text-white text-gray-900 text-center">
                    ¡Cuenta creada!
                </h2>
                <p className="text-center text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                    Hemos enviado un email de verificación a <strong>{email}</strong>.
                    <br />Revisa tu bandeja de entrada (y la carpeta de spam) y haz clic en el enlace para activar tu cuenta.
                </p>
                <Link to="/login">
                    <BrandButton variant="outline" size="md">Ir al login</BrandButton>
                </Link>
            </div>
        </>
    );

    const formContent = (
        <>
            <div>
                <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">
                    Crea tu cuenta
                </p>
                <h2 className="text-2xl md:text-3xl font-extrabold dark:text-white text-gray-900">
                    Registro
                </h2>
            </div>

            <form onSubmit={submit} noValidate className="flex flex-col gap-4">

                <TextInput
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                    autoComplete="name"
                    placeholder="Tu nombre"
                    leftIcon={<User size={16} />}
                />

                <TextInput
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="tu@email.com"
                    leftIcon={<Mail size={16} />}
                />

                <TextInput
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="Contraseña"
                    leftIcon={<Lock size={16} />}
                    rightSlot={
                        <button
                            type="button"
                            onClick={() => setShowPass(p => !p)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-base-content transition-colors"
                            tabIndex={-1}
                        >
                            {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    }
                />

                <TextInput
                    type={showPass2 ? 'text' : 'password'}
                    value={password2}
                    onChange={e => setPassword2(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="Repetir contraseña"
                    leftIcon={<Lock size={16} />}
                    rightSlot={
                        <button
                            type="button"
                            onClick={() => setShowPass2(p => !p)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-base-content transition-colors"
                            tabIndex={-1}
                        >
                            {showPass2 ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    }
                />

                <BrandButton variant="solid" size="md" type="submit" className="w-full justify-center">
                    Crear cuenta
                </BrandButton>
            </form>

            <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                ¿Ya tienes cuenta?{' '}
                <Link to="/login" className="text-brand-red hover:underline font-semibold">
                    Inicia sesión
                </Link>
            </p>
        </>
    );

    const content = registered ? successContent : formContent;

    return (
        <div className="flex flex-col md:flex-row h-full w-full">

            {/* ── MÓVIL ─────────────────────── */}
            <div className="md:hidden flex flex-col h-full w-full">
                <div className="flex-shrink-0 overflow-y-auto dark:bg-base-300/80 bg-white/85 backdrop-blur-md border-b border-white/10 dark:border-white/5 px-6 py-8 flex flex-col gap-5">
                    {content}
                </div>
                <div className="flex-1" />
            </div>

            {/* ── TABLET / DESKTOP ─────────── */}
            <div className="hidden md:flex flex-1 items-center justify-end w-full h-full px-8 lg:px-12">
                <SlideCard
                    direction="right"
                    className="glassCard rounded-2xl shadow-xl w-[50%] lg:w-[40%] max-h-[88vh] overflow-y-auto p-8 flex flex-col gap-5"
                >
                    {content}
                </SlideCard>
            </div>

        </div>
    );
}

export default Register;
